/* A pick-list with a search box at the top (client / project / task). Tapping the field opens the list; typing narrows it
   to the options containing every typed word (in any order); Enter or a tap picks. Recently used options come first.
   On a phone it opens as a sheet from the bottom, on a computer as a drop-down under the field. */
import {escapeHtml, syncScrollLock} from './common.ts';

export interface ComboOption { id: string; label: string; sub?: string; keywords?: string; recent?: boolean }

/** the field itself (a button that shows the chosen option) */
export function comboHtml(id: string, o: {label: string; placeholder: string; disabled?: boolean}): string {
  return `<button type="button" class="combo" id="${id}" ${o.disabled ? 'disabled' : ''}><span class="combo-label ${o.label ? '' : 'placeholder'}">${escapeHtml(o.label || o.placeholder)}</span><span class="combo-caret">▾</span></button>`;
}

let open: {close: () => void} | null = null;
export function closeCombo() { open?.close(); }

/** opens the list when the field is tapped; onPick gets the chosen id */
export function wireCombo(id: string, title: string, options: () => ComboOption[], onPick: (id: string) => void, empty?: () => string): void {
  const btn = document.getElementById(id) as HTMLButtonElement | null;
  if (!btn) return;
  btn.onclick = () => { if (!btn.disabled) openCombo(btn, title, options(), onPick, empty?.() || ''); };
}

function words(s: string) { return s.toLowerCase().split(/\s+/).filter(Boolean); }
function openCombo(anchor: HTMLElement, title: string, opts: ComboOption[], onPick: (id: string) => void, empty = '') {
  closeCombo();
  const panel = document.createElement('div');
  panel.className = 'combo-panel';
  panel.setAttribute('role', 'dialog');
  panel.innerHTML = `<div class="combo-head"><strong>${escapeHtml(title)}</strong><button type="button" class="icon-btn combo-x" aria-label="Close">✕</button></div>
    <input class="combo-search" type="search" placeholder="Type to search…" autocomplete="off" autocapitalize="off" spellcheck="false" enterkeyhint="done">
    <div class="combo-list" role="listbox"></div>`;
  const backdrop = document.createElement('div'); backdrop.className = 'combo-backdrop';
  document.body.append(backdrop, panel); syncScrollLock();
  const phone = window.matchMedia('(max-width: 640px)').matches;
  if (!phone) {   // drop-down under the field, kept inside the window
    const r = anchor.getBoundingClientRect(), w = Math.max(r.width, 320);
    panel.style.left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left)) + 'px';
    panel.style.width = w + 'px';
    const below = window.innerHeight - r.bottom;
    if (below >= 280 || below >= r.top) { panel.style.top = (r.bottom + 4) + 'px'; panel.style.maxHeight = Math.max(200, below - 16) + 'px'; }
    else { panel.style.bottom = (window.innerHeight - r.top + 4) + 'px'; panel.style.maxHeight = Math.max(200, r.top - 16) + 'px'; }
  } else panel.classList.add('sheet');
  const search = panel.querySelector<HTMLInputElement>('.combo-search')!, list = panel.querySelector<HTMLElement>('.combo-list')!;
  let shown: ComboOption[] = [], active = 0;
  const paint = () => {
    const q = words(search.value);
    const match = (o: ComboOption) => { const hay = (o.label + ' ' + (o.sub || '') + ' ' + (o.keywords || '')).toLowerCase(); return q.every(w => hay.includes(w)); };
    shown = opts.filter(match);
    if (!q.length) shown = [...shown.filter(o => o.recent), ...shown.filter(o => !o.recent)];
    active = Math.min(active, Math.max(0, shown.length - 1));
    const cap = shown.slice(0, 200), recentCount = q.length ? 0 : cap.filter(o => o.recent).length;
    list.innerHTML = cap.length ? cap.map((o, i) =>
      (i === 0 && recentCount ? '<div class="combo-group">Recent</div>' : '') + (i === recentCount && recentCount ? '<div class="combo-group">All</div>' : '') +
      `<div class="combo-opt ${i === active ? 'active' : ''}" role="option" data-i="${i}"><span>${escapeHtml(o.label)}</span>${o.sub ? `<span class="combo-sub">${escapeHtml(o.sub)}</span>` : ''}</div>`).join('') +
      (shown.length > cap.length ? `<div class="combo-more">…${shown.length - cap.length} more — keep typing</div>` : '')
      : `<div class="combo-more">${escapeHtml(!opts.length && empty ? empty : 'Nothing matches.')}</div>`;
    list.querySelectorAll<HTMLElement>('.combo-opt').forEach(el => el.onclick = () => pick(+el.dataset.i!));
  };
  const pick = (i: number) => { const o = shown[i]; if (!o) return; close(); onPick(o.id); };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); anchor.focus(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(shown.length - 1, active + 1); paint(); list.querySelector('.active')?.scrollIntoView({block: 'nearest'}); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(0, active - 1); paint(); list.querySelector('.active')?.scrollIntoView({block: 'nearest'}); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(active); }
  };
  function close() { panel.remove(); backdrop.remove(); syncScrollLock(); document.removeEventListener('keydown', onKey, true); if (open?.close === close) open = null; }
  search.oninput = () => { active = 0; paint(); };
  document.addEventListener('keydown', onKey, true);
  backdrop.onclick = close;
  panel.querySelector<HTMLElement>('.combo-x')!.onclick = close;
  open = {close};
  paint();
  search.focus();
}

/** update a field in place (inside an open window that is not redrawn) */
export function setCombo(id: string, label: string, placeholder: string, disabled = false): void {
  const btn = document.getElementById(id) as HTMLButtonElement | null; if (!btn) return;
  btn.disabled = disabled;
  const l = btn.querySelector('.combo-label')!; l.textContent = label || placeholder; l.classList.toggle('placeholder', !label);
}
